import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../components/AuthProvider";
import { useAvailableDates } from "./useAvailableDates";
import { API_BASE_URL } from "../utils/constants";
import { toast } from "react-toastify";

/**
 * Shared session hook for first, second, and third hearing pages.
 *
 * @param {number}   meetingNo              - 1 / 2 / 3, used for HearingVideos API
 * @param {string}   availableDatesEndpoint - e.g. "AvailableFirstHearingDates"
 * @param {Function} fetchCases             - async (formattedDate, arbId) => row[] (rows keep lotNo / clientId / productId)
 *
 * A hearing day can hold several lots (lot no + bank + product); the table, the video status and the
 * video upload all follow the selected lot.
 */

// Lot of a case row; lot fields are stored as text on cases
const lotKeyOf = (row) => `${row.lotNo}|${row.clientId}|${row.productId}`;
export function useHearingSession({ meetingNo, availableDatesEndpoint, fetchCases }) {
  const { user } = useAuth();
  const [arbId, setArbId] = useState(null);

  const [selectedDate, setSelectedDate] = useState(null);
  const [formattedDate, setFormattedDate] = useState(null);

  const [data, setData] = useState([]);
  const [selectedLotKey, setSelectedLotKey] = useState("");
  const [bankNames, setBankNames] = useState({});
  const [productNames, setProductNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Add Details modal
  const [show, setShow] = useState(false);
  const [caseId, setCaseId] = useState(null);

  // Global video upload modal
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [globalVideos, setGlobalVideos] = useState([]);
  const [videosUploaded, setVideosUploaded] = useState(false);
  const globalVideoInputRef = useRef(null);

  useEffect(() => {
    if (user?.[0]) setArbId(user[0].Ref_id);
  }, [user]);

  const { availableDates, availableDateObjects, openToDate } = useAvailableDates(availableDatesEndpoint, arbId);

  // Bank / product names for the lot labels
  useEffect(() => {
    const toMap = (rows, idKey, nameKey) => Object.fromEntries((rows || []).map((r) => [String(r[idKey]), r[nameKey]]));
    fetch(`${API_BASE_URL}/api/Client`).then((r) => (r.ok ? r.json() : [])).then((rows) => setBankNames(toMap(rows, "Client_id", "client_name"))).catch(() => {});
    fetch(`${API_BASE_URL}/api/products`).then((r) => (r.ok ? r.json() : [])).then((rows) => setProductNames(toMap(rows, "Product_id", "Product_name"))).catch(() => {});
  }, []);

  // Lots with cases on the selected day
  const lots = useMemo(() => {
    const byKey = new Map();
    data.forEach((row) => {
      const key = lotKeyOf(row);
      const lot = byKey.get(key) || { key, lotNo: row.lotNo, clientId: row.clientId, productId: row.productId, cases: 0 };
      lot.cases += 1;
      byKey.set(key, lot);
    });
    return [...byKey.values()].map((lot) => ({
      ...lot,
      label: [`Lot ${lot.lotNo}`, bankNames[String(lot.clientId)], productNames[String(lot.productId)]].filter(Boolean).join(" - "),
    }));
  }, [data, bankNames, productNames]);
  const selectedLot = lots.find((lot) => lot.key === selectedLotKey) || null;
  const filteredData = useMemo(
    () => (selectedLotKey ? data.filter((row) => lotKeyOf(row) === selectedLotKey) : []),
    [data, selectedLotKey]
  );

  // ── Date ──────────────────────────────────────────────────────────────────────

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setVideosUploaded(false);
    setData([]);
    setSelectedLotKey("");
    setCurrentPage(1);
    setFormattedDate(date ? date.toLocaleDateString("en-US") : null);
  };

  // ── Video status ──────────────────────────────────────────────────────────────

  const lotQuery = (lot) => `Lot_no=${lot.lotNo}&Client_id=${lot.clientId}&Product_id=${lot.productId}`;

  // Videos are kept per lot: re-check whenever the day or the lot changes
  useEffect(() => {
    setVideosUploaded(false);
    if (!formattedDate || !arbId || !selectedLot) return;
    fetch(
      `${API_BASE_URL}/api/HearingVideoStatus?Arb_id=${arbId}&Meeting_No=${meetingNo}&Hearing_date=${encodeURIComponent(formattedDate)}&${lotQuery(selectedLot)}`
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((result) => setVideosUploaded(result?.hasVideos === true))
      .catch(() => {}); // non-critical
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedDate, arbId, selectedLotKey, meetingNo]);

  const handleLotChange = (key) => {
    setSelectedLotKey(key);
    setCurrentPage(1);
  };

  // ── Fetch cases ───────────────────────────────────────────────────────────────

  const handleAppointments = async () => {
    if (!formattedDate) return;
    setLoading(true);
    try {
      const rows = await fetchCases(formattedDate, arbId);
      setData(rows);
      // Keep the chosen lot if it still has cases; pick it automatically when the day has only one lot
      const keys = [...new Set(rows.map(lotKeyOf))];
      setSelectedLotKey((current) => (keys.includes(current) ? current : keys.length === 1 ? keys[0] : ""));
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Failed to fetch appointments");
    } finally {
      setLoading(false);
    }
  };

  // ── Add Details modal ─────────────────────────────────────────────────────────

  const handleAddDetails = (id) => { setCaseId(id); setShow(true); };
  const handleClose = () => { setShow(false); setCaseId(null); };

  const handleSaveDetails = async ({ contestedDate, comment, attachmentFiles }) => {
    if (!caseId) { toast.error("Case ID is missing!"); return; }
    if (!contestedDate) { toast.error("Please select a contested date."); return; }
    setLoading(true);
    const formData = new FormData();
    attachmentFiles.forEach((file, index) => formData.append(`files[${index}]`, file));
    formData.append('Comment', comment || "");
    try {
      toast.info("Saving details, please wait...");
      const url = `${API_BASE_URL}/api/FirstHearing?Case_id=${caseId}&Meeting_No=${meetingNo}&Contested=true&Contested_Date=${encodeURIComponent(contestedDate.toLocaleDateString("en-US"))}`;
      const response = await fetch(url, { method: "POST", body: formData });
      if (response.ok) {
        await handleAppointments();
        setShow(false);
        setCaseId(null);
        toast.success("Details saved successfully", { theme: "colored", autoClose: 2000 });
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.message || "Failed to save details.", { theme: "colored" });
      }
    } catch (error) {
      toast.error("An error occurred: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Global video upload ───────────────────────────────────────────────────────

  const handleGlobalVideoChange = (e) => {
    setGlobalVideos((prev) => [...prev, ...Array.from(e.target.files)]);
  };

  const handleRemoveGlobalVideo = (index) => {
    setGlobalVideos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
    setGlobalVideos([]);
  };

  const handleGlobalVideoUpload = async () => {
    if (globalVideos.length === 0) { toast.error("Please select at least one video file."); return; }
    if (!formattedDate) { toast.error("No hearing date selected."); return; }
    if (!selectedLot) { toast.error("Choose the lot these videos are for."); return; }
    setLoading(true);
    const formData = new FormData();
    globalVideos.forEach((video, index) => formData.append(`videos[${index}]`, video));
    try {
      toast.info("Uploading videos, please wait...");
      const url = `${API_BASE_URL}/api/HearingVideos?Arb_id=${arbId}&Meeting_No=${meetingNo}&Hearing_date=${encodeURIComponent(formattedDate)}&${lotQuery(selectedLot)}`;
      const response = await fetch(url, { method: "POST", body: formData });
      if (response.ok) {
        setVideosUploaded(true);
        handleCloseVideoModal();
        toast.success("Videos uploaded successfully!", { theme: "colored", autoClose: 2000 });
      } else {
        const err = await response.json().catch(() => null);
        toast.error(err?.message || "Video upload failed.", { theme: "colored" });
      }
    } catch (error) {
      toast.error("An error occurred: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Pagination ────────────────────────────────────────────────────────────────

  const allDetailsSaved = filteredData.length > 0 && filteredData.every(item => item.detailsSaved);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const paginationPages = (() => {
    const pages = [];
    const window = 5;
    let start = Math.max(currentPage - Math.floor(window / 2), 1);
    let end = Math.min(start + window - 1, totalPages);
    if (end - start + 1 < window) start = Math.max(end - window + 1, 1);
    for (let n = start; n <= end; n++) pages.push({ n, active: n === currentPage });
    return pages;
  })();

  // ─────────────────────────────────────────────────────────────────────────────

  const clearData = () => {
    setData([]);
    setSelectedLotKey("");
    setVideosUploaded(false);
    setCurrentPage(1);
  };

  return {
    arbId,
    selectedDate, formattedDate,
    availableDates, availableDateObjects, openToDate,
    data, filteredData, loading,
    lots, selectedLotKey, selectedLot, handleLotChange,
    currentItems, currentPage, setCurrentPage, totalPages, paginationPages,
    show, caseId,
    showVideoModal, setShowVideoModal,
    globalVideos, globalVideoInputRef, videosUploaded, allDetailsSaved,
    handleDateChange, handleAppointments, clearData,
    handleAddDetails, handleClose, handleSaveDetails,
    handleGlobalVideoChange, handleRemoveGlobalVideo,
    handleCloseVideoModal, handleGlobalVideoUpload,
  };
}
